import { createSelector } from 'reselect';
import moment from 'moment';

import { RootState } from 'redux/schemas';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { getUniqueKeyForTimeSlots } from 'athena/helpers/utils';

export const getMentorshipProgramSessionsList = createSelector(
  (state: RootState) => state.models.mentorshipProgramSessions,
  (state, ids: number[]) => ids,
  (MentorshipProgramSessions: Record<number, MentorshipProgramSession>, ids: number[]) => ids.map((id) => MentorshipProgramSessions[id]),
);

export const getNextMentorshipProgramSession = (state: RootState, connectionId) => Object.values(state.models.mentorshipProgramSessions)
  .filter((session) => session.status === 'upcoming' && session.mentorshipProgramConnection.id === connectionId)
  .sort((sessionA, sessionB) => moment(sessionA.scheduledDate).diff(moment(sessionB.scheduledDate)))[0];

export const getResetMentoringProgramSessionsListState = (state: RootState) => state.app.mentoringProgram.sessions.resetList;

export const getMentorshipProgramSession = (sessionId: number) => (state: RootState) => state.models.mentorshipProgramSessions?.[sessionId];

export const getSessionActionItems = (sessionId: number) => (state: RootState) => {
  const session = state.models.mentorshipProgramSessions?.[sessionId];
  return session?.mentorshipProgramActionItems || [];
};

export const getMentorshipProgramSessionPlan = (connectionId: number) => (state: RootState) => (
  state.models.mentorshipProgramMeetingPlans?.[connectionId]
);

export const getResetMentoringProgramNextStepsState = (state: RootState) => state.app.mentoringProgram.nextSteps.resetNextSteps;

export const getIsSessionCreated = (enrollmentId: number, connectionId: number) => (state: RootState) => state.models.mentoringProgramEnrollments[
  enrollmentId
].assignedConnections.find((connection) => connection.connectionId === connectionId)?.isSessionCreated;

export const getCalendarIntegrationSettings = (programId: number, enrollmentId: number) => (state: RootState) => {
  const integration = state.app.mentoringProgram.msCalendarIntegration?.[programId]?.[enrollmentId];
  return integration || null;
};

export const getSessionAvailabilityByDay = (
  connectionId: number,
  programId: number,
  duration: number,
  day: string,
) => (state: RootState) => {
  const connectionSessionAvailability: {
    sessionAvailabilitySlots?: Record<number, any>
  } = state.models.mentorshipProgramConnections?.[connectionId] || {};
  const programSessionAvailability = connectionSessionAvailability?.sessionAvailabilitySlots?.[programId] || {};
  const durationSessionAvailability = programSessionAvailability?.[duration] || {};
  return durationSessionAvailability?.[day] || [];
};

export const hasSessionAvailabilityError = (state: RootState) => state.app.mentoringProgram.sessions.availability.error;

type LoadedProps = {
  mentorshipProgramConnectionId: number;
  mentorshipProgramId: number;
  meetingDuration: number;
  meetingDays: string[];
};

export const hasSessionAvailabilityLoadedForParams = (props: LoadedProps) => (state: RootState) => {
  const uniqueKey = getUniqueKeyForTimeSlots(props);
  return !!state.app.mentoringProgram.sessions.availability.hasBeenLoaded[uniqueKey];
};

export const getMentorshipProgramSessionIndex = (sessionId: number) => (state: RootState) => {
  const sessions = Object.values(state.models.mentorshipProgramSessions);
  const sortedSessions = sessions.slice().sort((a, b) => moment(a.scheduledDate).diff(moment(b.scheduledDate)));
  return sortedSessions.findIndex(session => session.id === sessionId) + 1;
};
